// src/components/CarFilterBar.tsx

import {
  Box,
  Button,
  TextField,
  Menu,
  MenuItem,
  IconButton,
  Typography,
  Slider,
  Stack,
  InputAdornment,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import SortIcon from '@mui/icons-material/Sort';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Custom Components
import RightDrawer from './RightDrawer';
import { RangeFilterMenu } from './RangeFilterMenu';
import axiosInstance from '../services/axiosInstance';
import { encodeId, decodeId } from '../utils/idEncoder';

// --- TYPES ---
type CarFilterBarProps = {
  viewMode?: 'grid' | 'list';
  onToggleViewMode?: () => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  url?: string;
  onFilterChange?: (key: string, value: string) => void;
};

// Define keys that should be encrypted
const ENCRYPTED_KEYS = [
  'vehicleBrandIds',
  'vehicleBodyTypeIds',
  'vehicleFuelTypeIds',
  'vehicleTransmissionTypeIds',
  'vehicleClassIds',
  'dealerIds'
];

interface ApiDataResponse {
  responseCode: string;
  responseData?: {
    dealerCompanyData?: any[];
    vehicleBodyTypeData?: any[];
    vehicleFuelTypeData?: any[];
    vehicleTransmissionTypeData?: any[];
    vehicleBrandData?: any[];
  }
}

const CarFilterBar = ({
  viewMode,
  onToggleViewMode,
  searchQuery,
  onSearchChange,
  url,
  onFilterChange,
}: CarFilterBarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // --- STATE ---
  const [dealerData, setDealerData] = useState<any[]>([]);
  const [fuelTypeData, setvehicleFuelTypeData] = useState<any[]>([]);
  const [TransmissionTypeData, setvehicleTransmissionTypeData] = useState<any[]>([]);
  const [bodyTypesData, setvehicleBodyTypeData] = useState<any[]>([]);
  const [brandData, setvehicleBrandData] = useState<any[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);

  // Temp state for checkboxes
  const [tempSelected, setTempSelected] = useState<{ [key: string]: string[] }>({});
  const [initializedKeys, setInitializedKeys] = useState<Set<string>>(new Set());

  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [sortList, setSortList] = useState<{ key: string; direction: 'asc' | 'desc' }[]>([]);

  // --- STYLES ---
  // Hides scrollbar but allows scrolling
  const noScrollbarStyle = {
    overflowX: 'auto',
    display: 'flex',
    gap: 1,
    width: '100%',
    '&::-webkit-scrollbar': { display: 'none' },
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
  };

  // --- SORTING LOGIC ---
  const handleSortClick = (event: React.MouseEvent<HTMLButtonElement>) => setSortAnchorEl(event.currentTarget);
  const handleSortClose = () => setSortAnchorEl(null);

  const handleSortChange = (key: string) => {
    setSortList((prev) => {
      const existing = prev.find((s) => s.key === key);
      let updated: { key: string; direction: 'asc' | 'desc' }[];
      if (existing) {
        const newDirection: 'asc' | 'desc' = existing.direction === 'asc' ? 'desc' : 'asc';
        updated = prev.map((s) => s.key === key ? { ...s, direction: newDirection } : s);
      } else {
        updated = [...prev, { key, direction: 'asc' }];
      }
      const params = new URLSearchParams(location.search);
      params.set('sortKeys', updated.map((s) => s.key).join(','));
      params.set('sortDirs', updated.map((s) => s.direction).join(','));
      params.delete('page');
      navigate(`${url ?? '/vehicle-listing'}?${params.toString()}`);
      return updated;
    });
    handleSortClose();
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const keys = params.get('sortKeys')?.split(',') || [];
    const dirs = params.get('sortDirs')?.split(',') || [];
    if (keys.length > 0) {
      setSortList(keys.map((key, i) => ({ key, direction: (dirs[i] === 'desc' ? 'desc' : 'asc') })));
    }
  }, [location.search]);

  // --- YEAR LOGIC ---
  const currentYear = new Date().getFullYear();
  const minYear = currentYear - 15;
  const maxYear = currentYear;
  const searchParams = new URLSearchParams(location.search);
  const [yearRange, setYearRange] = useState<number[]>([
    Number(searchParams.get('minManufacturingYear')) || minYear,
    Number(searchParams.get('maxManufacturingYear')) || maxYear,
  ]);
  const yearMarks = Array.from({ length: 6 }, (_, i) => ({ value: minYear + i * 3, label: (minYear + i * 3).toString() }));

  const handleYearChange = (_: any, newValue: number | number[]) => setYearRange(newValue as number[]);

  const applyYearFilter = () => {
    const [min, max] = yearRange;
    const params = new URLSearchParams(location.search);
    params.set('minManufacturingYear', min.toString());
    params.set('maxManufacturingYear', max.toString());
    params.delete('page');
    navigate(`${url ?? '/vehicle-listing'}?${params.toString()}`);
    handleClose();
  };

  const clearYearFilter = () => {
    const params = new URLSearchParams(location.search);
    params.delete('minManufacturingYear');
    params.delete('maxManufacturingYear');
    navigate(`${url ?? '/vehicle-listing'}?${params.toString()}`);
    setYearRange([minYear, maxYear]);
    handleClose();
  };

  // --- API DATA ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        const payload = { dealerCompanyData: true, vehicleBodyTypeData: true, vehicleFuelTypeData: true, vehicleTransmissionTypeData: true, vehicleBrandData: true };
        const res = await axiosInstance.post("/CustomerVehicle/VehicleMultipleDataList", payload);
        const data = res.data as ApiDataResponse;
        if (data.responseCode === "00" && data.responseData) {
          setvehicleBodyTypeData(data.responseData.vehicleBodyTypeData || []);
          setvehicleFuelTypeData(data.responseData.vehicleFuelTypeData || []);
          setvehicleTransmissionTypeData(data.responseData.vehicleTransmissionTypeData || []);
          setvehicleBrandData(data.responseData.vehicleBrandData || []);
          setDealerData(data.responseData.dealerCompanyData || []);
        }
      } catch (e) { console.error(e); }
    };
    fetchData();
  }, []);

  // --- UI HELPERS ---
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>, filter: string) => {
    event.stopPropagation();
    setSelectedLabel(filter);
    setAnchorEl(event.currentTarget);
    setActiveFilter(filter);
  };
  const handleClose = () => { setAnchorEl(null); setActiveFilter(null); };

  // --- CHECKBOX LOGIC (With Decoding) ---
  const getTempSelected = (key: string): string[] => {
    if (!initializedKeys.has(key)) {
      const params = new URLSearchParams(location.search);
      const val = params.get(key);
      const parts = val ? val.split(',') : [];

      // Decode URL hashes back to IDs for the internal checkbox state
      let decodedIds = parts;
      if (ENCRYPTED_KEYS.includes(key)) {
        decodedIds = parts.map(hash => decodeId(hash)?.toString() || '').filter(Boolean);
      }

      setTempSelected(prev => ({ ...prev, [key]: decodedIds }));
      setInitializedKeys(prev => new Set(prev).add(key));
      return decodedIds;
    }
    return tempSelected[key] || [];
  };

  const toggleSelection = (key: string, id: string) => {
    setTempSelected(prev => {
      const set = new Set(prev[key] || []);
      if (set.has(id)) set.delete(id); else set.add(id);
      return { ...prev, [key]: Array.from(set) };
    });
  };

  // Encode IDs before sending to URL
  const applySelection = (key: string) => {
    const rawIds = tempSelected[key] || [];
    let outputString = "";

    if (ENCRYPTED_KEYS.includes(key)) {
      // Convert "1" -> "X9"
      const encoded = rawIds.map(id => encodeId(id));
      outputString = encoded.join(',');
    } else {
      outputString = rawIds.join(',');
    }

    if (onFilterChange) onFilterChange(key, outputString);
    handleClose();
  };

  const clearSelection = (key: string) => {
    setTempSelected(prev => ({ ...prev, [key]: [] }));
    if (onFilterChange) onFilterChange(key, '');
    handleClose();
  };

  const renderCheckboxFilterMenu = (key: string, data: any[], idField: string, labelField: string) => (
    <Box sx={{ width: 220, maxHeight: 300, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, px: 1, py: 1, overflowY: 'auto' }}>
        {data.map((item) => {
          const id = item[idField].toString();
          // Checkboxes use the Raw ID (e.g. "1")
          const checked = getTempSelected(key).includes(id);
          return (
            <MenuItem key={id} onClick={() => toggleSelection(key, id)} disableRipple>
              <input type="checkbox" checked={checked} readOnly style={{ marginRight: 8 }} />
              <Typography>{item[labelField]}</Typography>
            </MenuItem>
          );
        })}
      </Box>
      <Box sx={{ borderTop: '1px solid #eee', p: 1, position: 'sticky', bottom: 0, bgcolor: '#fff' }}>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" onClick={() => clearSelection(key)}>Clear</Button>
          <Button size="small" variant="contained" onClick={() => applySelection(key)}>Apply</Button>
        </Stack>
      </Box>
    </Box>
  );

  const filterButtons = ['Brand & Model', 'Body Type', 'Fuel Type', 'Transmission', 'Vehicle Status', 'Car Price & Instalment', 'Year', 'Mileage', 'Depreciation'];
  const filterMap: any = {
    'Body Type': { key: 'vehicleBodyTypeIds', data: bodyTypesData, idField: 'vehicleBodyTypeId' },
    'Brand & Model': { key: 'vehicleBrandIds', data: brandData, idField: 'vehicleBrandId' },
    'Fuel Type': { key: 'vehicleFuelTypeIds', data: fuelTypeData, idField: 'vehicleFuelTypeId' },
    'Transmission': { key: 'vehicleTransmissionTypeIds', data: TransmissionTypeData, idField: 'vehicleTransmissionTypeId' },
    'Vehicle Status': { key: 'statusId', data: [{ statusId: 'VPB', description: 'Available' }, { statusId: 'VSL', description: 'Sold' }], idField: 'statusId' },
    'Car Price & Instalment': { key: 'minAskingPrice', getLabel: (p: URLSearchParams) => p.get('minAskingPrice') ? `$${Number(p.get('minAskingPrice')).toLocaleString()}+` : null },
    'Year': { key: 'minManufacturingYear', getLabel: (p: URLSearchParams) => p.get('minManufacturingYear') ? `${p.get('minManufacturingYear')}+` : null },
    'Mileage': { key: 'minMileage', getLabel: (p: URLSearchParams) => p.get('minMileage') ? `${Number(p.get('minMileage')).toLocaleString()}km+` : null },
    'Depreciation': { key: 'minDepreciation', getLabel: (p: URLSearchParams) => p.get('minDepreciation') ? `$${Number(p.get('minDepreciation')).toLocaleString()}+` : null },
  };

  // --- RENDER ---
  return (
    <Box sx={{
      p: 2,
      backgroundColor: '#f9f9f9',
      borderRadius: 2,
      // On mobile, remove border radius to make it feel like a native app header
      ...(isMobile && { p: 1.5, borderRadius: 0, borderBottom: '1px solid #eee' })
    }}>
      
      {/* --- TOP ROW: Search & Main Actions --- */}
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          fullWidth
          placeholder="Search Make or Model..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          size="small"
          sx={{
            bgcolor: '#fff',
            '& .MuiOutlinedInput-root': { borderRadius: 3 } // Rounder search bar
          }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
            endAdornment: searchQuery && (
              <IconButton size="small" onClick={() => { onSearchChange(''); navigate(url || '/vehicle-listing'); }}>
                <ClearIcon fontSize="small" />
              </IconButton>
            )
          }}
        />

        {/* Action Buttons: Condensed on Mobile */}
        {isMobile ? (
          <IconButton
            onClick={() => navigate(`${location.pathname}?vehicleModel=${searchQuery}`)}
            sx={{ bgcolor: 'primary.main', color: 'white', borderRadius: 2, '&:hover': { bgcolor: 'primary.dark' } }}
          >
            <SearchIcon />
          </IconButton>
        ) : (
          <>
            <Button variant="contained" onClick={() => navigate(`${location.pathname}?vehicleModel=${searchQuery}`)} sx={{ borderRadius: 3 }}>
              Search
            </Button>
            <Button variant="outlined" color="error" startIcon={<RestartAltIcon />} onClick={() => { onSearchChange(''); navigate(url || '/vehicle-listing'); }} sx={{ borderRadius: 3 }}>
              Reset
            </Button>
          </>
        )}
      </Stack>

      {/* --- MIDDLE ROW: Horizontal Scrollable Filters --- */}
      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
        
        {/* View Toggle (Pinned Left) */}
        {onToggleViewMode && (
          <Box sx={{ mr: 1, flexShrink: 0 }}>
            <IconButton onClick={onToggleViewMode} sx={{ bgcolor: '#fff', border: '1px solid #eee' }} size="small">
              {viewMode === 'grid' ? <ViewListIcon /> : <ViewModuleIcon />}
            </IconButton>
          </Box>
        )}

        {/* Horizontal Scroll Container */}
        <Box sx={{ ...noScrollbarStyle, mx: isMobile ? -1.5 : 0, px: isMobile ? 1.5 : 0 }}>
          {filterButtons.map(label => {
            const conf = filterMap[label];
            let subLabel = null;
            let isActive = false;

            if (conf) {
              const params = new URLSearchParams(location.search);
              if (conf.getLabel) subLabel = conf.getLabel(params);
              else {
                // To show count, we check presence of param
                const count = (params.get(conf.key)?.split(',') || []).filter(Boolean).length;
                if (count > 0) subLabel = `${count}`;
              }
              if (subLabel) isActive = true;
            }

            const isMenuOpen = selectedLabel === label && Boolean(anchorEl);

            return (
              <Button
                key={label}
                variant={isActive || isMenuOpen ? "contained" : "outlined"}
                size="small"
                onClick={(e) => handleClick(e, label)}
                sx={{
                  borderRadius: 5,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  minWidth: 'auto',
                  px: 2,
                  py: 0.5,
                  textTransform: 'none',
                  bgcolor: (isActive || isMenuOpen) ? '#FFA100' : '#fff',
                  color: (isActive || isMenuOpen) ? '#000' : '#444',
                  borderColor: (isActive || isMenuOpen) ? 'transparent' : '#e0e0e0',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#FFB300', borderColor: 'transparent', boxShadow: 'none' }
                }}
              >
                {label}
                {subLabel && (
                  <Box
                    component="span"
                    sx={{
                      ml: 1,
                      bgcolor: 'rgba(0,0,0,0.1)',
                      px: 0.8,
                      borderRadius: 1,
                      fontSize: '0.7rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {subLabel}
                  </Box>
                )}
              </Button>
            );
          })}
        </Box>
      </Box>

      {/* --- BOTTOM ROW: Utilities (Sort, All Filters, Clear) --- */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: 2, pt: 1.5, borderTop: '1px solid #e0e0e0' }}
      >
        <Stack direction="row" spacing={1}>
          <Button
            variant="text"
            size="small"
            startIcon={<TuneIcon fontSize="small" />}
            onClick={() => setDrawerOpen(true)}
            sx={{ color: '#555' }}
          >
            Filters
          </Button>

          <Button
            variant="text"
            size="small"
            startIcon={<SortIcon fontSize="small" />}
            onClick={handleSortClick}
            sx={{ color: '#555' }}
          >
            {sortList.length ? 'Sorted' : 'Sort'}
          </Button>
        </Stack>

        <Button
          variant="text"
          size="small"
          color="error"
          startIcon={<DeleteOutlineIcon fontSize="small" />}
          onClick={() => { setSortList([]); navigate(url || '/vehicle-listing'); }}
        >
          {isMobile ? "Clear" : "Clear Filters"}
        </Button>
      </Stack>

      {/* --- MENUS --- */}
      <Menu anchorEl={sortAnchorEl} open={Boolean(sortAnchorEl)} onClose={handleSortClose}>
        {['askingPrice', 'addDate', 'mileage', 'vehicleModel'].map(k => (
          <MenuItem key={k} onClick={() => handleSortChange(k)}>
            {k === 'askingPrice' ? 'Price' : k === 'addDate' ? 'Date Listed' : k}
            {sortList.find(s => s.key === k) && (
              sortList.find(s => s.key === k)?.direction === 'asc'
                ? <ArrowUpwardIcon fontSize="small" sx={{ ml: 1 }} />
                : <ArrowDownwardIcon fontSize="small" sx={{ ml: 1 }} />
            )}
          </MenuItem>
        ))}
      </Menu>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose} PaperProps={{ sx: { width: 300, maxHeight: 400, mt: 1, borderRadius: 2 } }}>
        {activeFilter === 'Brand & Model' && renderCheckboxFilterMenu('vehicleBrandIds', brandData, 'vehicleBrandId', 'description')}
        {activeFilter === 'Body Type' && renderCheckboxFilterMenu('vehicleBodyTypeIds', bodyTypesData, 'vehicleBodyTypeId', 'description')}
        {activeFilter === 'Fuel Type' && renderCheckboxFilterMenu('vehicleFuelTypeIds', fuelTypeData, 'vehicleFuelTypeId', 'description')}
        {activeFilter === 'Transmission' && renderCheckboxFilterMenu('vehicleTransmissionTypeIds', TransmissionTypeData, 'vehicleTransmissionTypeId', 'description')}
        {activeFilter === 'Vehicle Status' && renderCheckboxFilterMenu('statusId', [{ statusId: 'VPB', description: 'Available' }, { statusId: 'VSL', description: 'Sold' }], 'statusId', 'description')}
        {activeFilter === 'Year' && (
          <Box p={3}>
            <Typography gutterBottom fontWeight="bold">Manufacturing Year</Typography>
            <Box px={1} py={2}>
              <Slider value={yearRange} onChange={handleYearChange} valueLabelDisplay="auto" min={minYear} max={maxYear} marks={yearMarks} />
            </Box>
            <Stack direction="row" spacing={1} justifyContent="flex-end" mt={2}>
              <Button size="small" onClick={clearYearFilter}>Clear</Button>
              <Button size="small" variant="contained" onClick={applyYearFilter}>Apply</Button>
            </Stack>
          </Box>
        )}
      </Menu>

      {/* External Components */}
      <RangeFilterMenu anchorEl={anchorEl} activeFilter={activeFilter || ''} filterKey="Car Price & Instalment" minParamKey="minAskingPrice" maxParamKey="maxAskingPrice" min={1000} max={9999999} label="Price Range ($)" onClose={handleClose} />
      <RangeFilterMenu anchorEl={anchorEl} activeFilter={activeFilter || ''} filterKey="Depreciation" minParamKey="minDepreciation" maxParamKey="maxDepreciation" min={1000} max={100000} label="Depreciation ($)" onClose={handleClose} />
      <RangeFilterMenu anchorEl={anchorEl} activeFilter={activeFilter || ''} filterKey="Mileage" minParamKey="minMileage" maxParamKey="maxMileage" min={0} max={300000} label="Mileage (km)" onClose={handleClose} />

      <RightDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} fuelTypeData={fuelTypeData} TransmissionTypeData={TransmissionTypeData} bodyTypesData={bodyTypesData} brandData={brandData} dealerData={dealerData} />
    </Box>
  );
};

export default CarFilterBar;